import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import {
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  NgbDate,
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDateStruct,
} from '@ng-bootstrap/ng-bootstrap';
import { DateTime } from 'luxon';

/**
 * This Service handles how the date is represented in scripts i.e.ngModel.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomAdapter extends NgbDateAdapter<string> {
  readonly DELIMITER = '-';

  fromModel(value: null | string): NgbDateStruct | null {
    if (value) {
      const date = value.split(this.DELIMITER);
      return {
        month: Number.parseInt(date[0], 10),
        day: Number.parseInt(date[1], 10),
        year: Number.parseInt(date[2], 10),
      };
    }

    return null;
  }

  toModel(date: NgbDateStruct | null): null | string {
    return date
      ? `${date.month}${this.DELIMITER}${date.day}${this.DELIMITER}${date.year}`
      : null;
  }
}

/**
 * This Service handles how the date is rendered and parsed from keyboard i.e. in the bound input field.
 */
@Injectable()
export class CustomDateParserFormatter extends NgbDateParserFormatter {
  readonly DELIMITER = '/';

  parse(value: string): NgbDateStruct | null {
    if (value) {
      const date = value.split(this.DELIMITER);
      return {
        month: Number.parseInt(date[0], 10),
        day: Number.parseInt(date[1], 10),
        year: Number.parseInt(date[2], 10),
      };
    }
    return null;
  }

  format(date: NgbDateStruct | null): string {
    return date
      ? `${date.month}${this.DELIMITER}${date.day}${this.DELIMITER}${date.year}`
      : '';
  }
}

@Injectable()
export class NgbStringAdapter extends NgbDateAdapter<Date> {
  fromModel(date: Date): NgbDateStruct {
    date = new Date(date);
    return {
      year: date.getFullYear(),
      month: date.getMonth(),
      day: date.getDate(),
    };
  }

  toModel(date: NgbDateStruct): Date {
    return new Date(date.year, date.month, date.day);
  }
}

@Injectable()
export class DatepickerService {
  dateRangeValidator(
    startControlName: string,
    endControlName: string,
  ): ValidatorFn {
    return (group: UntypedFormGroup): null | ValidationErrors => {
      const startTime = group.get(startControlName);
      const endTime = group.get(endControlName);

      if (!startTime || !endTime) {
        return null;
      }

      const startTimeValue =
        this.toISOStringInUTC(startTime.value as string) ?? '';
      const endTimeValue = this.toISOStringInUTC(endTime.value as string) ?? '';

      if (
        (startTime.errors && !startTime.errors.inCorrectDate) ||
        (endTime.errors && !endTime.errors.inCorrectDate)
      ) {
        return null;
      }
      if (
        this.fromISOString(endTimeValue) > this.fromISOString(startTimeValue)
      ) {
        startTime.setErrors(null);
        endTime.setErrors(null);
      } else {
        startTime.setErrors({ inCorrectDate: true });
        endTime.setErrors({ inCorrectDate: true });
      }

      return null;
    };
  }

  toNgbDate(date: InputDateTypes, parseFormat?: string): NgbDateStruct {
    const d = this.toDateTime(date, parseFormat) ?? DateTime.now();

    return {
      year: d.year,
      month: d.month,
      day: d.day,
    };
  }

  toISOStringInUTC(
    date: InputDateTypes,
    keepLocalTime = true,
    parseFormat?: string,
  ): string | undefined {
    return (
      this.toDateTime(date, parseFormat)
        ?.setZone('utc', { keepLocalTime })
        .toISO() ?? undefined
    );
  }

  fromISOString(dateString: string): DateTime {
    return DateTime.fromISO(dateString, { zone: 'utc' });
  }

  toDateTime(date: InputDateTypes, parseFormat?: string): DateTime | undefined {
    if (!date) {
      return undefined;
    }

    if (date instanceof DateTime) {
      return date as DateTime;
    }

    if (date instanceof NgbDate) {
      const jsdate = new Date(date.year, date.month - 1, date.day);
      return DateTime.fromJSDate(jsdate);
    }

    if (date instanceof Date) {
      return DateTime.fromJSDate(date);
    }

    let dateTime = DateTime.fromFormat(
      date,
      parseFormat ? parseFormat : 'dd/MM/yyyy',
    );
    if (!dateTime.isValid) {
      dateTime = this.fromISOString(date);
    }

    return dateTime;
  }

  formatDate(date: Date | string) {
    const pipe = new DatePipe('en-US');
    return pipe.transform(date, 'MM/dd/yyyy') ?? '';
  }
}

export type InputDateTypes = Date | DateTime | NgbDate | string;
